export const PAYMENT_ISSUE_DISMISS_PREFIX = 'dathent.paymentIssueBanner.dismissed.';

export function paymentIssueDismissKey(paymentIssue) {
  const scope = paymentIssue?.invoiceId || paymentIssue?.status || 'unknown';
  return `${PAYMENT_ISSUE_DISMISS_PREFIX}${scope}`;
}

export function shouldHidePaymentIssue(paymentIssue, storage = globalThis?.sessionStorage) {
  try {
    return storage?.getItem(paymentIssueDismissKey(paymentIssue)) === '1';
  } catch (_) {
    return false;
  }
}

export function dismissPaymentIssue(paymentIssue, storage = globalThis?.sessionStorage) {
  try {
    storage?.setItem(paymentIssueDismissKey(paymentIssue), '1');
  } catch (_) { /* private mode */ }
}

export function paymentIssueReasonKey(code) {
  switch (String(code || '').toLowerCase()) {
    case 'insufficient_funds': return 'insufficientFunds';
    case 'card_declined':
    case 'generic_decline':
    case 'do_not_honor': return 'cardDeclined';
    case 'authentication_required': return 'authenticationRequired';
    case 'expired_card': return 'cardExpired';
    default: return null;
  }
}

export function paymentIssueLinks(paymentIssue, currentUrl) {
  return {
    payNow: paymentIssue?.invoiceUrl || null,
    portalReturnUrl: currentUrl || null,
    billingSettings: '/app/settings?tab=billing',
  };
}

function formatAmount(amountDue, currency, locale) {
  if (!Number.isInteger(amountDue) || amountDue < 0 || !currency) return null;
  try {
    return new Intl.NumberFormat(locale?.startsWith('en') ? 'en-GB' : (locale || 'en-GB'), {
      style: 'currency',
      currency: String(currency).toUpperCase(),
    }).format(amountDue / 100);
  } catch (_) {
    return null;
  }
}

// TASK-331 (M3 fix): exported so UpgradeModal/Plans.jsx can label the
// "pay open invoice" renew CTA with the real amount, reusing the exact same
// currency formatting `buildPaymentIssueCopy` already uses for the dunning
// banner — no second implementation to drift.
export function formatPaymentIssueAmount(paymentIssue, locale = 'en') {
  return formatAmount(paymentIssue?.amountDue, paymentIssue?.currency, locale);
}

function formatRetryDate(value, locale) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const resolvedLocale = locale?.startsWith('en') ? 'en-GB' : (locale || 'en-GB');
  const formatter = new Intl.DateTimeFormat(resolvedLocale, { day: 'numeric', month: 'short' });
  if (resolvedLocale === 'en-GB') {
    const parts = formatter.formatToParts(date);
    const day = parts.find((part) => part.type === 'day')?.value;
    const month = parts.find((part) => part.type === 'month')?.value?.slice(0, 3);
    return day && month ? `${day} ${month}` : formatter.format(date);
  }
  return formatter.format(date);
}

// TASK-331: readPaymentIssue (gateway) widened `kind` from dunning-only to also
// cover a lapsed org (canceled, or a dunning status past the grace window).
// buildPaymentIssueCopy branches on it FIRST — a lapsed org must never see the
// dunning "Pay now / Update card, retry on <date>" copy, which implies the
// subscription is still alive and simply needs a payment retried. Fail-soft:
// no `kind` field (old backend) falls straight through to the untouched
// dunning branch below — byte-identical.
export function isLapsedPaymentIssue(paymentIssue) {
  return paymentIssue?.kind === 'lapsed';
}

export function isDunningPaymentIssue(paymentIssue) {
  return Boolean(paymentIssue && !isLapsedPaymentIssue(paymentIssue)
    && ['past_due', 'incomplete', 'unpaid'].includes(paymentIssue.status));
}

// Human reason line for the lapsed banner/notice — payment_failed / unpaid /
// canceled (the three reasons the owner named), sourced from the widened
// readPaymentIssue's `reason` field. Returns null for an unrecognized/missing
// reason so callers can omit the line rather than render nothing useful.
export function lapsedReasonCopy(paymentIssue, t, locale = 'en') {
  const reason = paymentIssue?.reason || null;
  if (reason === 'payment_failed') return t('lapsed.reasonPaymentFailed');
  if (reason === 'unpaid') return t('lapsed.reasonUnpaid');
  if (reason === 'canceled') {
    const date = formatRetryDate(paymentIssue?.lapsedAt, locale);
    return date ? t('lapsed.reasonCanceled', { date }) : t('lapsed.reasonCanceled', { date: '' });
  }
  return null;
}

export function buildPaymentIssueCopy(paymentIssue, planName, t, locale = 'en') {
  if (isLapsedPaymentIssue(paymentIssue)) {
    // No plan-name variant is defined for the lapsed title/body (see (e)'s
    // paymentIssue.lapsedTitle/lapsedBody) — the resolved plan surfaces on the
    // Renew button label instead (lapsed.renewPlan). `planName` is unused here.
    return { title: t('paymentIssue.lapsedTitle'), body: t('paymentIssue.lapsedBody') };
  }

  const amount = formatAmount(paymentIssue?.amountDue, paymentIssue?.currency, locale);
  const reasonKey = paymentIssueReasonKey(paymentIssue?.declineCode);
  const reason = reasonKey ? t(`paymentIssue.reasons.${reasonKey}`) : null;
  let declined;

  if (amount && planName && reason) declined = t('paymentIssue.declinedAmountPlanReason', { amount, plan: planName, reason });
  else if (amount && planName) declined = t('paymentIssue.declinedAmountPlan', { amount, plan: planName });
  else if (amount && reason) declined = t('paymentIssue.declinedAmountReason', { amount, reason });
  else if (amount) declined = t('paymentIssue.declinedAmount', { amount });
  else if (reason) declined = t('paymentIssue.declinedReason', { reason });
  else declined = t('paymentIssue.declinedGeneric');

  const retryDate = formatRetryDate(paymentIssue?.nextPaymentAttempt, locale);
  const resolution = retryDate
    ? t('paymentIssue.retry', { date: retryDate })
    : t('paymentIssue.resolve');

  return { title: t('paymentIssue.title'), body: `${declined} ${resolution}` };
}
